import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Pencil } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import { fetchStoredBankTransactions } from '@/lib/open-banking';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppEmptyState,
  AppInlineAlert,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  applySharedHomeBillInferences,
  buildSharedHomeBillDefaults,
  buildSharedHomeBillInferences,
  deriveVehicleServiceDueDate,
  findVehicleInsuranceRenewal,
  formatSharedHomeBillAmount,
  formatSharedHomeBillSchedule,
  getSharedHomeBillPortalUrl,
  getSharedHomeBillLabel,
  mergeSharedHomeBillRecords,
  normalizeVehicleRegistration,
  type SharedHomeVehicleRecord,
  type SharedVehicleInsuranceRenewal,
  type SharedHomeBillInference,
  type SharedHomeBillKey,
  type SharedHomeBillRecord,
} from '../../../ui-foundation';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import { repairStoredHomeVehicle } from '@/lib/homeVehicles';

type HomeProfileBillContext = {
  address: string | null;
  tenure: string | null;
  monthly_rent: number | null;
  landlord_name: string | null;
  council_tax_band: string | null;
  mortgage_provider: string | null;
  ground_rent_amount: number | null;
  service_charge_amount: number | null;
  has_vehicle: boolean | null;
  vehicle_registration_number: string | null;
  vehicle_make: string | null;
  vehicle_mot_due_date: string | null;
  vehicle_mot_status: string | null;
  vehicle_tax_due_date: string | null;
  vehicle_tax_status: string | null;
  vehicle_last_synced_at: string | null;
  utility_gas_provider: string | null;
  utility_electric_provider: string | null;
  utility_broadband_provider: string | null;
  utility_water_provider: string | null;
  utility_phone_provider: string | null;
};

type HomeVehicleRow = {
  id: string;
  registration_number: string;
  make: string | null;
  mot_due_date: string | null;
  mot_status: string | null;
  tax_due_date: string | null;
  tax_status: string | null;
  tax_annual_cost: number | null;
  tax_six_month_cost: number | null;
  tax_monthly_direct_debit_total: number | null;
  tax_monthly_direct_debit_installment: number | null;
  mot_estimated_cost: number | null;
  service_last_date: string | null;
  service_due_date: string | null;
  dvla_synced_at: string | null;
};

type VehicleInsuranceRenewalRow = {
  type: string;
  renewal_date: string;
  registration_number: string | null;
  vehicle_label: string | null;
  provider: string | null;
};

const GBP_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatCurrency(value: number | null | undefined) {
  return value == null ? null : GBP_FORMATTER.format(value);
}

type HomeBillRow = {
  bill_key: SharedHomeBillKey;
  enabled: boolean;
  frequency: SharedHomeBillRecord['frequency'];
  amount: number | null;
  due_day_of_week: SharedHomeBillRecord['dueDayOfWeek'];
  due_day_of_month: number | null;
  annual_due_date: string | null;
  provider_name: string | null;
  provider_account: string | null;
};

type BankTransactionRow = {
  booking_date: string;
  amount: number;
  description: string | null;
  creditor_name: string | null;
  account_id: string;
};

type BankAccountRow = {
  id: string;
  institution_name: string | null;
};

type BillsSectionKey = 'bills' | 'vehicle';

type BillsTabProps = {
  sectionOrder?: BillsSectionKey[];
  forcedOpenSection?: BillsSectionKey | null;
};

export default function BillsTab({
  sectionOrder = ['bills', 'vehicle'],
  forcedOpenSection = null,
}: BillsTabProps) {
  const navigate = useNavigate();
  const { user } = useApp();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<HomeProfileBillContext | null>(null);
  const [bills, setBills] = useState<SharedHomeBillRecord[]>([]);
  const [billInferences, setBillInferences] = useState<Map<SharedHomeBillKey, SharedHomeBillInference>>(new Map());
  const [vehicles, setVehicles] = useState<SharedHomeVehicleRecord[]>([]);
  const [vehicleInsuranceRenewals, setVehicleInsuranceRenewals] = useState<SharedVehicleInsuranceRenewal[]>([]);
  const [openSection, setOpenSection] = useState<BillsSectionKey | null>(null);

  const fetchBills = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setBills([]);
      setBillInferences(new Map());
      setLoading(false);
      return;
    }

    try {
      const [{ data: profileRow }, { data: billRows }, bankTransactions, { data: bankAccounts }, { data: vehicleRows }, { data: renewalRows }] = await Promise.all([
        supabase
          .from('home_profiles')
          .select('address, tenure, monthly_rent, landlord_name, council_tax_band, mortgage_provider, ground_rent_amount, service_charge_amount, has_vehicle, vehicle_registration_number, vehicle_make, vehicle_mot_due_date, vehicle_mot_status, vehicle_tax_due_date, vehicle_tax_status, vehicle_last_synced_at, utility_gas_provider, utility_electric_provider, utility_broadband_provider, utility_water_provider, utility_phone_provider')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('home_bill_settings')
          .select('bill_key, enabled, frequency, amount, due_day_of_week, due_day_of_month, annual_due_date, provider_name, provider_account')
          .eq('user_id', user.id),
        fetchStoredBankTransactions(user.id, {
          negativeOnly: true,
          select: 'booking_date, amount, description, creditor_name, account_id',
        }),
        (supabase as any)
          .from('bank_accounts')
          .select('id, institution_name')
          .eq('user_id', user.id),
        (supabase as any)
          .from('home_vehicles')
          .select('id, registration_number, make, mot_due_date, mot_status, tax_due_date, tax_status, tax_annual_cost, tax_six_month_cost, tax_monthly_direct_debit_total, tax_monthly_direct_debit_installment, mot_estimated_cost, service_last_date, service_due_date, dvla_synced_at')
          .eq('user_id', user.id)
          .order('sort_order', { ascending: true }),
        (supabase as any)
          .from('renewals')
          .select('type, renewal_date, registration_number, vehicle_label, provider')
          .eq('user_id', user.id)
          .eq('type', 'car-insurance')
          .order('renewal_date', { ascending: true }),
      ]);

      const nextProfile = (profileRow as HomeProfileBillContext | null) || null;
      const defaults = buildSharedHomeBillDefaults({
        tenure: nextProfile?.tenure || null,
        monthlyHousingCost: nextProfile?.monthly_rent ?? null,
        mortgageProvider: nextProfile?.mortgage_provider || null,
        landlordName: nextProfile?.landlord_name || null,
        councilTaxBand: nextProfile?.council_tax_band || null,
        groundRentAmount: nextProfile?.ground_rent_amount ?? null,
        serviceChargeAmount: nextProfile?.service_charge_amount ?? null,
        hasVehicle: nextProfile?.has_vehicle ?? null,
        vehicleRegistrationNumber: nextProfile?.vehicle_registration_number || null,
        vehicleTaxDueDate: nextProfile?.vehicle_tax_due_date || null,
        vehicleMotDueDate: nextProfile?.vehicle_mot_due_date || null,
        utilityGasProvider: nextProfile?.utility_gas_provider || null,
        utilityElectricProvider: nextProfile?.utility_electric_provider || null,
        utilityBroadbandProvider: nextProfile?.utility_broadband_provider || null,
        utilityWaterProvider: nextProfile?.utility_water_provider || null,
        utilityPhoneProvider: nextProfile?.utility_phone_provider || null,
      });
      const storedBills = ((billRows || []) as HomeBillRow[]).map((row) => ({
        billKey: row.bill_key,
        enabled: row.enabled,
        frequency: row.frequency,
        amount: row.amount == null ? null : Number(row.amount),
        dueDayOfWeek: row.due_day_of_week,
        dueDayOfMonth: row.due_day_of_month,
        annualDueDate: row.annual_due_date,
        providerName: row.provider_name,
        providerAccount: row.provider_account,
      }));
      const baseBills = mergeSharedHomeBillRecords(defaults, storedBills);
      const institutionNames = new Map(((bankAccounts || []) as BankAccountRow[]).map((row) => [row.id, row.institution_name || null]));
      const inferredBills = buildSharedHomeBillInferences(
        ((bankTransactions || []) as BankTransactionRow[]).map((row) => ({
          bookingDate: row.booking_date,
          amount: Number(row.amount || 0),
          description: row.description,
          creditorName: row.creditor_name,
          accountInstitutionName: institutionNames.get(row.account_id) || null,
        })),
        baseBills,
      );

      setProfile(nextProfile);
      setBills(applySharedHomeBillInferences(baseBills, inferredBills));
      setBillInferences(inferredBills);
      setVehicles(
        await Promise.all(
          ((vehicleRows || []) as HomeVehicleRow[]).map((row) =>
            repairStoredHomeVehicle({
              id: row.id,
              registrationNumber: row.registration_number,
              make: row.make,
              motDueDate: row.mot_due_date,
              motStatus: row.mot_status,
              taxDueDate: row.tax_due_date,
              taxStatus: row.tax_status,
              taxAnnualCost: row.tax_annual_cost == null ? null : Number(row.tax_annual_cost),
              taxSixMonthCost: row.tax_six_month_cost == null ? null : Number(row.tax_six_month_cost),
              taxMonthlyDirectDebitTotal: row.tax_monthly_direct_debit_total == null ? null : Number(row.tax_monthly_direct_debit_total),
              taxMonthlyDirectDebitInstallment: row.tax_monthly_direct_debit_installment == null ? null : Number(row.tax_monthly_direct_debit_installment),
              motEstimatedCost: row.mot_estimated_cost == null ? null : Number(row.mot_estimated_cost),
              serviceLastDate: row.service_last_date,
              serviceDueDate: row.service_due_date,
              dvlaSyncedAt: row.dvla_synced_at,
            }),
          ),
        ),
      );
      setVehicleInsuranceRenewals(
        ((renewalRows || []) as VehicleInsuranceRenewalRow[]).map((row) => ({
          type: row.type,
          renewalDate: row.renewal_date,
          registrationNumber: row.registration_number,
          vehicleLabel: row.vehicle_label,
          provider: row.provider,
        })),
      );
    } catch (error) {
      console.error(error);
      setProfile(null);
      setBills([]);
      setBillInferences(new Map());
      setVehicles([]);
      setVehicleInsuranceRenewals([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchBills();
  }, [fetchBills]);

  useEffect(() => {
    const handleRefresh = () => {
      void fetchBills();
    };

    window.addEventListener('adulting:data-changed', handleRefresh);
    return () => window.removeEventListener('adulting:data-changed', handleRefresh);
  }, [fetchBills]);

  useEffect(() => {
    if (!forcedOpenSection) return;
    setOpenSection(forcedOpenSection);
  }, [forcedOpenSection]);

  const activeBills = useMemo(() => bills.filter((bill) => bill.enabled), [bills]);
  const vehicleRegistration = normalizeVehicleRegistration(profile?.vehicle_registration_number || '');
  const vehicleCards = useMemo(() => {
    if (vehicles.length === 0) {
      if (!profile?.has_vehicle || !vehicleRegistration) return [];
      return [{
        registrationNumber: vehicleRegistration,
        make: profile.vehicle_make || null,
        motDueDate: profile.vehicle_mot_due_date || null,
        motStatus: profile.vehicle_mot_status || null,
        taxDueDate: profile.vehicle_tax_due_date || null,
        taxStatus: profile.vehicle_tax_status || null,
        taxAnnualCost: null,
        taxSixMonthCost: null,
        taxMonthlyDirectDebitTotal: null,
        taxMonthlyDirectDebitInstallment: null,
        motEstimatedCost: null,
        serviceLastDate: null,
        serviceDueDate: null,
        dvlaSyncedAt: profile.vehicle_last_synced_at || null,
      }];
    }
    return vehicles;
  }, [profile, vehicleRegistration, vehicles]);
  const toggleSection = (section: BillsSectionKey) => {
    setOpenSection((current) => (current === section ? null : section));
  };

  if (loading) {
    return <AppLoadingState title="Loading bills" body="Pulling the household costs and vehicle dates saved for this home." />;
  }

  if (!profile?.address) {
    return (
      <AppEmptyState
        title="No home details yet"
        body="Save your home details first, then switch the standard bills on here."
        action={(
          <AppButton onPress={() => navigate('/home?tab=my-home&edit=bills')}>
            <Pencil size={16} /> Edit home details
          </AppButton>
        )}
      />
    );
  }

  const billsSection = (
      <SectionAccordionCard
        key="bills"
        emoji="🧾"
        title="Bills"
        subtitle="Keep the regular household costs visible in one calmer place."
        open={openSection === 'bills'}
        onToggle={() => toggleSection('bills')}
      >
        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title="Bills"
              subtitle="Keep the regular household costs visible without leaving Home."
              action={(
                <AppButton size="sm" tone="ghost" onPress={() => navigate('/home?tab=my-home&edit=bills')}>
                  <Pencil size={12} /> Edit bills
                </AppButton>
              )}
            />
          </AppCardHeader>
          <AppCardBody className="space-y-3">
            <AppText fontSize="$sm" color="$colorMuted">
              {activeBills.length
                ? `${activeBills.length} bill${activeBills.length === 1 ? '' : 's'} switched on for this home.`
                : 'No standard bills are switched on for this home yet.'}
            </AppText>

            {activeBills.length ? (
              <div className="space-y-2">
                {activeBills.map((bill) => (
                  <div key={bill.billKey} className="rounded-2xl border border-border bg-muted/20 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <AppText fontSize="$sm" fontWeight="700">{getSharedHomeBillLabel(bill.billKey)}</AppText>
                        <AppText fontSize="$xs" color="$colorMuted">
                          {formatSharedHomeBillSchedule(bill)}
                        </AppText>
                      </div>
                      {formatSharedHomeBillAmount(bill.amount, bill.frequency) ? (
                        <AppText fontSize="$xs" fontWeight="700">
                          {formatSharedHomeBillAmount(bill.amount, bill.frequency)}
                        </AppText>
                      ) : null}
                    </div>
                    {billInferences.get(bill.billKey) ? (
                      <AppText fontSize="$xs" color="$colorMuted" mt="$xs">
                        {billInferences.get(bill.billKey)?.summary}
                      </AppText>
                    ) : null}
                    {bill.providerName ? (
                      <AppText fontSize="$xs" color="$colorMuted" mt="$xs">
                        Provider: {bill.providerName}
                      </AppText>
                    ) : null}
                    {bill.providerAccount ? (
                      <AppText fontSize="$xs" color="$colorMuted" mt="$xs">
                        Account / reference: {bill.providerAccount}
                      </AppText>
                    ) : null}
                    {getSharedHomeBillPortalUrl(bill.billKey, bill.providerName) ? (
                      <AppButton
                        size="sm"
                        tone="ghost"
                        mt="$sm"
                        onPress={() => window.open(getSharedHomeBillPortalUrl(bill.billKey, bill.providerName) || '', '_blank', 'noopener,noreferrer')}
                      >
                        Open provider link
                      </AppButton>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <AppEmptyState
                title="No bills switched on yet"
                body="Use Edit bills to turn on the standard bills you want Adulting to keep in view."
                action={(
                  <AppButton size="sm" tone="outline" onPress={() => navigate('/home?tab=my-home&edit=bills')}>
                    <Pencil size={14} /> Edit bills
                  </AppButton>
                )}
              />
            )}
          </AppCardBody>
        </AppCard>
      </SectionAccordionCard>
  );

  const vehicleSection = (vehicleCards.length > 0 || profile.has_vehicle) ? (
        <SectionAccordionCard
          key="vehicle"
          emoji="🚗"
          title="Vehicles"
          subtitle="Keep MOT and tax dates with the rest of Home admin."
          open={openSection === 'vehicle'}
          onToggle={() => toggleSection('vehicle')}
        >
          <AppCard className="space-y-3">
            <AppSectionHeader
              title="Vehicle details"
              subtitle="Adulting keeps the registration with the annual vehicle dates that matter at home."
            />

            {vehicleCards.length ? (
              <div className="space-y-2">
                {vehicleCards.map((vehicle) => {
                  const insuranceRenewal = findVehicleInsuranceRenewal(vehicleInsuranceRenewals, vehicle.registrationNumber);
                  const serviceDueDate = deriveVehicleServiceDueDate(vehicle.serviceLastDate, vehicle.serviceDueDate);
                  return (
                    <div key={vehicle.id || vehicle.registrationNumber} className="rounded-2xl border border-border bg-muted/30 p-3">
                      <AppText fontSize="$xs" color="$colorMuted">
                        {[vehicle.make, vehicle.registrationNumber].filter(Boolean).join(' • ') || 'Vehicle saved'}
                      </AppText>
                      <AppText fontSize="$xs" color="$colorMuted">
                        MOT: {vehicle.motDueDate ? format(parseISO(vehicle.motDueDate), 'd MMM yyyy') : 'Not set'}
                        {vehicle.motStatus ? ` • ${vehicle.motStatus}` : ''}
                        {formatCurrency(vehicle.motEstimatedCost) ? ` • guide ${formatCurrency(vehicle.motEstimatedCost)}` : ''}
                      </AppText>
                      <AppText fontSize="$xs" color="$colorMuted">
                        Car tax: {vehicle.taxDueDate ? format(parseISO(vehicle.taxDueDate), 'd MMM yyyy') : 'Not set'}
                        {vehicle.taxStatus ? ` • ${vehicle.taxStatus}` : ''}
                      </AppText>
                      {(vehicle.taxAnnualCost != null || vehicle.taxSixMonthCost != null || vehicle.taxMonthlyDirectDebitInstallment != null) ? (
                        <AppText fontSize="$xs" color="$colorMuted">
                          Estimated tax: {formatCurrency(vehicle.taxAnnualCost) || '—'} annual
                          {vehicle.taxSixMonthCost != null ? ` • ${formatCurrency(vehicle.taxSixMonthCost)} for 6 months` : ''}
                          {vehicle.taxMonthlyDirectDebitInstallment != null ? ` • about ${formatCurrency(vehicle.taxMonthlyDirectDebitInstallment)}/month by Direct Debit` : ''}
                        </AppText>
                      ) : null}
                      <AppText fontSize="$xs" color="$colorMuted">
                        Service due: {serviceDueDate ? format(parseISO(serviceDueDate), 'd MMM yyyy') : 'Not set yet'}
                        {vehicle.serviceLastDate ? ` • last service ${format(parseISO(vehicle.serviceLastDate), 'd MMM yyyy')}` : ''}
                      </AppText>
                      <AppText fontSize="$xs" color="$colorMuted">
                        Insurance renewal: {insuranceRenewal ? format(parseISO(insuranceRenewal.renewalDate), 'd MMM yyyy') : 'Track this in Renewals'}
                        {insuranceRenewal?.provider ? ` • ${insuranceRenewal.provider}` : ''}
                      </AppText>
                      {vehicle.dvlaSyncedAt ? (
                        <AppText fontSize="$xs" color="$colorMuted">
                          Last synced {format(parseISO(vehicle.dvlaSyncedAt), 'd MMM yyyy')}
                        </AppText>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}

            {profile.has_vehicle && !vehicleRegistration ? (
              <AppInlineAlert title="Add the vehicle registration to pull DVLA dates">
                Adulting can bring through car tax and MOT dates once the registration number is saved in Home details.
              </AppInlineAlert>
            ) : null}
          </AppCard>
        </SectionAccordionCard>
  ) : null;

  const sections: Record<BillsSectionKey, ReactNode> = {
    bills: billsSection,
    vehicle: vehicleSection,
  };

  return (
    <div className="space-y-3">
      {sectionOrder.map((section) => sections[section]).filter(Boolean)}
    </div>
  );
}
